import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { AbstractTrackingService } from "./abstract-tracking-service";
import type { Me } from "@/generated/graphql";
import type { TrackEventEnum, TrackOptions } from "./tracking-types";
import type {
  ChannelMetadata,
  ChannelAndUserInChannelMetadata,
} from "@/models";

function isAmplitudePropertyValue(
  value: unknown,
): value is string | number | boolean | Array<string | number> {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    (Array.isArray(value) &&
      value.every(
        (item) => typeof item === "string" || typeof item === "number",
      ))
  );
}

export class AmplitudeService extends AbstractTrackingService {
  name = "AmplitudeService";
  private apiKey: string;

  constructor(apiKey: string) {
    super();
    this.apiKey = apiKey;
  }

  public async init(channelMetadata: ChannelMetadata) {
    await super.init(channelMetadata);

    amplitude.init(this.apiKey, {
      autocapture: true,
    });

    // Ensure channel gets identified on init
    amplitude.identify(this.buildIdentityWithChannelMetadata(channelMetadata));

    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate: 1,
    });
    amplitude.add(sessionReplayTracking);
  }

  public identifyChannel(channelMetadata: ChannelMetadata) {
    amplitude.identify(this.buildIdentityWithChannelMetadata(channelMetadata));
  }

  public identifyUser(
    user: Me,
    channelUserMetadata?: ChannelAndUserInChannelMetadata,
  ) {
    const identifyEvent = new amplitude.Identify();

    if (user) {
      Object.entries(user).forEach(([key, value]) => {
        if (isAmplitudePropertyValue(value)) {
          identifyEvent.set(key, value);
        }
      });
      amplitude.setUserId(user.id);
    }

    if (channelUserMetadata) {
      Object.entries(channelUserMetadata).forEach(([key, value]) => {
        if (isAmplitudePropertyValue(value)) {
          identifyEvent.set(key, value);
        }
      });
    }

    amplitude.identify(identifyEvent);
  }

  public resetIdentity() {
    amplitude.reset();
  }

  public track<T extends TrackEventEnum>(options: TrackOptions<T>) {
    amplitude.logEvent(options.event, {
      ...("metadata" in options ? options.metadata : {}),
      category: options.category,
    });
  }

  private buildIdentityWithChannelMetadata(channelMetadata: ChannelMetadata) {
    const identifyEvent = new amplitude.Identify();

    Object.entries(channelMetadata).forEach(([key, value]) => {
      identifyEvent.set(key, value);
    });

    return identifyEvent;
  }
}
